import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, parseMoney, parseQuantity, sumarDias, type Milli } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { DIAS_DE_PROVEEDORES, MESES_DE_INDICE, comprasPorProveedor, indiceDeCompras, ofertaPorVolumen, quienLoVendeMasBarato } from '../lib/abasto.js';
import { precioDeLinea, type DineroDeAnalisis, type Tramos } from '../lib/analisis.js';
import { DIAS_DE_RITMO, salidaPorDia } from '../lib/estante.js';
import { ventasDelPeriodo } from '../lib/libros.js';
import { localeActual, useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Campo } from './Campo.js';
import { ElegirEscribiendo } from './ElegirEscribiendo.js';
import { Barras, Cambio } from './Graficas.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { AProducto, Cargando, Corta, DetalleDeCifra, Fila, Nada, Seccion, pct, useFechas } from './PiezasDeAnalisis.js';

/**
 * «Compras» in «Análisis» (the owner, 2026-10-13, of what the page could add: «let's build all of
 * it»): what is bought from each supplier and owed to them, who sells each product cheapest, how
 * much dearer what the shop buys has become, and whether an offer to buy more at once pays. The
 * arithmetic is `lib/abasto.ts`.
 */
export function VistaCompras({ tramos, hoyEs, dinero }: { tramos: Tramos; hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const nombre = useNombreProducto();
  const { actual } = tramos;
  const [abierto, setAbierto] = useState<string | null>(null);
  const [oferta, setOferta] = useState(false);
  const datos = useLiveQuery(async () => {
    const [compras, proveedores, productos] = await Promise.all([db.compras.toArray(), db.proveedores.toArray(), db.productos.toArray()]);
    return { compras, proveedores: new Map(proveedores.map((p) => [p.id, p.nombre])), productos: new Map(productos.map((p) => [p.id, p])) };
  }, []);
  const calculo = useMemo(() => {
    if (!datos) return null;
    return {
      porProveedor: comprasPorProveedor(datos.compras, actual),
      baratos: quienLoVendeMasBarato(datos.compras, hoyEs),
      indice: indiceDeCompras(datos.compras, hoyEs),
    };
  }, [datos, actual, hoyEs]);
  if (!datos || !calculo) return <Cargando />;
  const { porProveedor, baratos, indice } = calculo;
  const proveedor = (id: string) => datos.proveedores.get(id) ?? '—';
  const comprado = porProveedor.reduce((a, x) => a + x.total, 0);
  const cuantas = porProveedor.reduce((a, x) => a + x.compras, 0);
  const mes = new Intl.DateTimeFormat(localeActual(), { month: 'short', year: '2-digit', timeZone: 'UTC' });

  return (
    <div className="flex flex-col gap-4" data-analisis="compras">
      <Seccion titulo={t('analisis.proveedores.titulo')} ayuda={t('analisis.proveedores.ayuda')} datos="proveedores">
        {porProveedor.length === 0 ? (
          <Nada texto={t('analisis.proveedores.nada')} />
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-300" data-proveedores-total>
              {t('analisis.proveedores.total', { monto: dinero(comprado), n: cuantas, m: porProveedor.filter((x) => x.total > 0).length })}
            </p>
            <Barras
              filas={porProveedor.map((x) => ({
                clave: x.proveedorId,
                etiqueta: proveedor(x.proveedorId),
                valor: x.total,
                texto: dinero(x.total),
                // The share under the name, not beside the amount: a supplier's name is long, and beside
                // both it broke in the middle of a word at 320 px.
                detalle: [pct(x.parteBp), t('analisis.proveedores.compras', { n: x.compras }), x.debe > 0 ? t('analisis.proveedores.debe', { monto: dinero(x.debe) }) : null]
                  .filter(Boolean)
                  .join(' · '),
                onAbrir: x.compras > 0 ? () => setAbierto(x.proveedorId) : undefined,
              }))}
              datos="proveedores"
            />
          </>
        )}
        {abierto !== null && (
          <DetalleDeCifra
            titulo={proveedor(abierto)}
            datos={`proveedor-${abierto}`}
            vacio={t('analisis.proveedores.nada')}
            onCerrar={() => setAbierto(null)}
            renglones={datos.compras
              .filter((c) => !c.anulada && c.proveedorId === abierto && c.fecha >= actual.desde && c.fecha <= actual.hasta)
              .sort((a, b) => b.fecha.localeCompare(a.fecha))
              .map((c) => ({
                clave: c.id,
                titulo: [f.conDia(c.fecha), c.documento].filter(Boolean).join(' · '),
                detalle: [t('analisis.proveedores.lineas', { n: c.lineas.length }), c.formaPago === 'credito' ? t('analisis.proveedores.alCredito') : null].filter(Boolean).join(' · '),
                valor: dinero(c.totalCordobas - (c.devuelto ?? 0)),
              }))}
          />
        )}
      </Seccion>

      <Seccion titulo={t('analisis.barato.titulo')} ayuda={t('analisis.barato.ayuda', { n: DIAS_DE_PROVEEDORES / 30 })} datos="mas-barato">
        {baratos.length === 0 ? (
          <Nada texto={t('analisis.barato.nada')} />
        ) : (
          <Corta filas={baratos}>
            {(vistas) => (
              <ul className="-my-2.5 divide-y divide-slate-800">
                {vistas.map((x) => {
                  const p = datos.productos.get(x.productoId);
                  return (
                    <Fila
                      key={x.productoId}
                      nombre={x.productoId}
                      a={<AProducto id={x.productoId} nombre={p ? nombre(p) : x.productoId} />}
                      detalle={t('analisis.barato.detalle', {
                        ultimo: proveedor(x.ultimo.proveedorId),
                        precio: dinero.porUnidad(x.ultimo.precio),
                        barato: proveedor(x.barato.proveedorId),
                        precioBarato: dinero.porUnidad(x.barato.precio),
                        fecha: f.corta(x.barato.fecha),
                        pct: pct(x.menosBp),
                      })}
                      valor={t('analisis.barato.alMes', { monto: dinero(x.ahorroAlMes) })}
                    />
                  );
                })}
              </ul>
            )}
          </Corta>
        )}
      </Seccion>

      <Seccion titulo={t('analisis.indice.titulo')} ayuda={t('analisis.indice.ayuda')} datos="indice">
        {indice.totalBp === null ? (
          <Nada texto={t('analisis.indice.nada')} />
        ) : (
          <>
            <p className="text-sm text-slate-300" data-indice-total>
              {indice.totalBp > 0
                ? t('analisis.indice.subio', { pct: pct(indice.totalBp), n: MESES_DE_INDICE })
                : indice.totalBp < 0
                  ? t('analisis.indice.bajo', { pct: pct(-indice.totalBp), n: MESES_DE_INDICE })
                  : t('analisis.indice.igual', { n: MESES_DE_INDICE })}
            </p>
            <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm min-[380px]:grid-cols-3 sm:grid-cols-4" data-indice-meses>
              {indice.meses.map((m) => (
                <li key={m.mes} className="flex items-baseline justify-between gap-2 rounded-lg bg-slate-800/60 px-2 py-1.5" data-mes={m.mes}>
                  <span className="whitespace-nowrap text-slate-400">{mes.format(new Date(`${m.mes}-01T00:00:00Z`))}</span>
                  {m.cambioBp === null ? (
                    <span className="text-xs text-slate-600">—</span>
                  ) : (
                    <span className="whitespace-nowrap">
                      <Cambio bp={m.cambioBp} alReves />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Seccion>

      <Seccion
        titulo={t('analisis.oferta.titulo')}
        ayuda={t('analisis.oferta.ayuda')}
        datos="oferta"
        accion={
          <button type="button" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" onClick={() => setOferta(true)} data-oferta-abrir>
            <Ico icono="calcular" className="size-4" />
            {t('analisis.oferta.boton')}
          </button>
        }
      >
        <p className="text-sm text-slate-400">{t('analisis.oferta.explica')}</p>
        {oferta && <OfertaPorVolumen hoyEs={hoyEs} onCerrar={() => setOferta(false)} />}
      </Seccion>
    </div>
  );
}

/** What a field typed holds, as an amount: null while empty, undefined when it is not one. */
function leer<T>(texto: string, como: (s: string) => T | null): T | null | undefined {
  if (texto.trim() === '') return null;
  const x = como(texto);
  return x === null || (x as number) <= 0 ? undefined : x;
}

/** «¿Conviene la oferta?»: an offer to buy more at once, tried in a sheet. Prices are typed in córdobas, as purchases are. */
function OfertaPorVolumen({ hoyEs, onCerrar }: { hoyEs: string; onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  const datos = useLiveQuery(async () => {
    const desde = sumarDias(hoyEs, -(DIAS_DE_RITMO - 1));
    const [productos, compras, existencias, ventas, reempaques, primera] = await Promise.all([
      db.productos.toArray(),
      db.compras.toArray(),
      db.existencias.toArray(),
      ventasDelPeriodo({ desde, hasta: hoyEs }),
      db.reempaques.where('fecha').aboveOrEqual(desde).toArray(),
      db.ventas.orderBy('fecha').first(),
    ]);
    // The last price each product was bought at, for one base unit.
    const ultimo = new Map<string, number>();
    for (const c of compras.filter((c) => !c.anulada).sort((a, b) => a.fecha.localeCompare(b.fecha) || a.id.localeCompare(b.id))) {
      for (const l of c.lineas) {
        const precio = precioDeLinea(l);
        if (precio !== null) ultimo.set(l.productoId, precio);
      }
    }
    return {
      productos: productos.filter((p) => p.activo && ultimo.has(p.id)),
      ultimo,
      existencias,
      ritmo: salidaPorDia({ ventas, reempaques, hoy: hoyEs, primeraVenta: primera?.fecha ?? null }),
    };
  }, [hoyEs]);
  const [productoId, setProductoId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [precio, setPrecio] = useState('');
  const [siempre, setSiempre] = useState('');
  const opciones = useMemo(() => (datos ? datos.productos.map((p) => ({ id: p.id, nombre: nombre(p) })) : []), [datos, nombre]);

  const producto = datos?.productos.find((p) => p.id === productoId);
  const unidad = producto ? etiquetaUnidad(producto.unidadBase, producto.unidadBase) : '';
  const n = leer(cantidad, parseQuantity);
  const oferta = leer(precio, parseMoney);
  const usual = leer(siempre, parseMoney);
  const listo = datos && producto && n && oferta && usual;
  const o = listo
    ? ofertaPorVolumen({
        cantidad: n as Milli,
        precioOferta: oferta,
        precioDeSiempre: usual,
        porDia: datos.ritmo.get(producto.id) ?? 0,
        existencia: datos.existencias.filter((e) => e.productoId === producto.id).reduce((a, e) => a + e.cantidad, 0),
      })
    : null;

  return (
    <Modal titulo={t('analisis.oferta.titulo')} onCerrar={onCerrar}>
      <div className="flex flex-col gap-4" data-oferta>
        <ElegirEscribiendo
          etiqueta={t('analisis.simular.producto')}
          placeholder={t('analisis.simular.buscar')}
          opciones={opciones}
          value={productoId}
          onChange={(id) => {
            setProductoId(id);
            const antes = datos?.ultimo.get(id);
            setSiempre(antes !== undefined ? (Math.round(antes) / 100).toFixed(2) : '');
          }}
          datos="oferta-producto"
        />
        {producto && (
          <>
            <Campo
              etiqueta={t('analisis.oferta.cuantas', { unidad })}
              inputMode="decimal"
              autoComplete="off"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              error={n === undefined ? t('analisis.oferta.malCantidad') : null}
              data-oferta-cantidad
            />
            <Campo
              etiqueta={t('analisis.oferta.precio', { unidad })}
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              error={oferta === undefined ? t('analisis.simular.mal') : null}
              data-oferta-precio
            />
            <Campo
              etiqueta={t('analisis.oferta.siempre', { unidad })}
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={siempre}
              onChange={(e) => setSiempre(e.target.value)}
              error={usual === undefined ? t('analisis.simular.mal') : null}
              ayuda={t('analisis.oferta.siempreAyuda')}
              data-oferta-siempre
            />
          </>
        )}
        {o && producto && (
          <div className="flex flex-col gap-1 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300" data-oferta-resultado>
            {o.ahorro > 0 ? (
              <p className="font-medium" data-oferta-ahorro>
                {t('analisis.oferta.ahorra', { invertido: formatMoney(o.invertido), ahorro: formatMoney(o.ahorro), pct: pct(o.ahorroBp) })}
              </p>
            ) : (
              <p className="font-medium text-rose-400" data-oferta-ahorro>
                {t('analisis.oferta.noAhorra', { invertido: formatMoney(o.invertido) })}
              </p>
            )}
            {o.diasTotal === null ? (
              <p data-oferta-dias>{t('analisis.oferta.sinRitmo', { n: DIAS_DE_RITMO })}</p>
            ) : (
              <p data-oferta-dias>{t('analisis.oferta.dias', { n: o.diasTotal })}</p>
            )}
            {o.rindeBp !== null && o.ahorro > 0 && (
              <p className="text-emerald-300" data-oferta-rinde>
                {t('analisis.oferta.rinde', { pct: pct(o.rindeBp) })}
              </p>
            )}
            {o.diasTotal !== null && producto.perecedero && o.diasTotal > 30 && (
              <p className="text-amber-300" data-oferta-aviso="perecedero">
                {t('analisis.oferta.perecedero', { n: o.diasTotal })}
              </p>
            )}
            {o.diasTotal !== null && o.diasTotal > 90 && (
              <p className="text-amber-300" data-oferta-aviso="largo">
                {t('analisis.oferta.largo')}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
